import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Archive,
  Check,
  Eye,
  FilePenLine,
  History,
  ImageOff,
  Percent,
  RotateCcw,
  Search,
  ShieldCheck,
  ShoppingBag,
  Store,
  X,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageShell } from "@/pages/dashboard/RolePages";
const TABS = [
  ["review", "Productos en revisión", Eye],
  ["catalog", "Catálogo Marketplace", ShoppingBag],
  ["edit", "Editar productos", FilePenLine],
  ["store", "Visibilidad en tienda", Store],
  ["landing", "Visibilidad en landing", Eye],
  ["commissions", "Comisiones", Percent],
  ["integrity", "Auditoría contable", ShieldCheck],
  ["history", "Historial", History],
];
const labels = {
  pending_review: "Pendiente de revisión",
  approved: "Aprobado",
  rejected: "Rechazado",
  draft: "Borrador",
  pending: "Pendiente",
  paid: "Pagado",
  preparing: "En preparación",
  shipped: "Enviado",
  delivered: "Entregado",
};
const energy = (v) => `⚡ ${Number(v || 0).toLocaleString("es-CO")} Energía KD9`,
  date = (v) => (v ? new Date(v).toLocaleString("es-CO") : "Sin fecha");
const url = (v) => {
  if (!v || /^https?:\/\//.test(v)) return v;
  return (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + v;
};
const Modal = ({ title, children, close, wide }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <button className="absolute inset-0 bg-black/80" onClick={close} />
    <section
      className={`kd9-governance-modal kd9-card relative z-10 w-full ${wide ? "max-w-5xl" : "max-w-xl"} max-h-[92dvh] overflow-y-auto p-4 sm:p-6`}
    >
      <div className="flex justify-between mb-5">
        <h2 className="font-cinzel kd9-gold-text text-xl">{title}</h2>
        <button onClick={close}>
          <X />
        </button>
      </div>
      {children}
    </section>
  </div>
);
const Product = ({ p, children }) => (
  <article className="kd9-governance-product kd9-card p-3 grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
    <div className="flex gap-3 items-center">
      <div className="w-16 h-16 bg-black/30 rounded overflow-hidden">
        {p.images?.[0] ? (
          <img
            src={url(p.images[0])}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <ImageOff className="m-5" />
        )}
      </div>
      <div>
        <h3 className="font-cinzel">{p.name}</h3>
        <p className="text-[10px] opacity-60">
          {p.creator_name} · {p.category}
        </p>
        <p className="text-[10px]">
          {labels[p.status] || p.status} · {energy(p.energy_cost)} · Comisión{" "}
          {p.commission_percent}% {p.archived_at ? "· Archivado" : ""}
        </p>
      </div>
    </div>
    <div className="kd9-governance-actions grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">{children}</div>
  </article>
);
export default function MarketplaceModeration() {
  const { accessMap } = useAuth(),
    perms = accessMap?.permissions || [],
    director = perms.includes("marketplace.manage"),
    canDecide = perms.includes("marketplace.products.decide");
  const [tab, setTab] = useState("review"),
    [data, setData] = useState({
      summary: {},
      products: [],
      history: [],
      orders: [],
    }),
    [modal, setModal] = useState(null),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [busy, setBusy] = useState(""),
    [query, setQuery] = useState(""),
    [integrity, setIntegrity] = useState({ summary: {}, items: [], total: 0 });
  const load = useCallback(async () => {
    try {
      const r = await api.get(
        director
          ? "/api/director/marketplace/governance"
          : "/api/admin/marketplace/products",
      );
      setData(
        director
          ? r.data
          : {
              summary: { pending_review: r.data.length },
              products: r.data,
              history: [],
            },
      );
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [director]);
  useEffect(() => {
    load();
  }, [load]);
  const loadIntegrity = useCallback(async () => {
    if (!director) return;
    try {
      const r = await api.get("/api/director/marketplace/integrity?limit=150");
      setIntegrity(r.data);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [director]);
  useEffect(() => {
    if (tab === "integrity") loadIntegrity();
  }, [tab, loadIntegrity]);
  const run = async (fn, msg) => {
    try {
      setBusy("saving");
      setError("");
      await fn();
      await load();
      setNotice(msg);
      setModal(null);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy("");
    }
  };
  const rows = useMemo(() => {
    const visible = tab === "review"
      ? data.products.filter((p) => p.status === "pending_review" && !p.archived_at)
      : data.products;
    const filtered = visible.filter((p) => `${p.name} ${p.creator_name} ${p.category} ${p.slug}`.toLowerCase().includes(query.toLowerCase()));
    return [...filtered].sort((a, b) => {
      if (tab !== "review") return String(b.updated_at || "").localeCompare(String(a.updated_at || ""));
      return String(a.submitted_at || a.updated_at || "").localeCompare(String(b.submitted_at || b.updated_at || ""));
    });
  }, [data.products, tab, query]);
  const openProduct = async (p, type = "detail") => {
    try {
      const r = await api.get(`/api/admin/marketplace/products/${p.slug}`);
      setModal({ type, ...r.data });
    } catch (e) {
      setError(formatApiError(e));
    }
  };
  const decide = (e) => {
    e.preventDefault();
    let m = modal;
    if (m.type === "return")
      return run(
        () => api.post(`/api/director/marketplace/products/${m.product.slug}/return`, { reason: m.text }),
        "Producto devuelto al creador para correcciones.",
      );
    if (m.type === "approve")
      return run(
        () =>
          api.post(
            `/api/director/marketplace/products/${m.product.slug}/approve`,
            { notes: m.text },
          ),
        "Producto aprobado, publicado y notificado.",
      );
    if (m.type === "reject")
      return run(
        () =>
          api.post(
            `/api/director/marketplace/products/${m.product.slug}/reject`,
            { reason: m.text },
          ),
        "Producto rechazado y notificado.",
      );
    if (m.type === "archive")
      return run(
        () =>
          api.delete(`/api/director/marketplace/products/${m.product.slug}`, {
            data: { reason: m.text },
          }),
        "Producto archivado lógicamente.",
      );
    return run(
      () =>
        api.post(
          `/api/director/marketplace/products/${m.product.slug}/restore`,
          { reason: m.text },
        ),
      "Producto restaurado y oculto.",
    );
  };
  const edit = (e) => {
    e.preventDefault();
    let p = modal.product;
    return run(
      () =>
        api.put(`/api/director/marketplace/products/${p.slug}`, {
          name: p.name,
          description: p.description,
          category: p.category,
          energy_cost: Number(p.energy_cost),
          stock: Number(p.stock),
          images: p.images || [],
          shipping_note: p.shipping_note || "",
          reason: modal.text,
        }),
      "Ficha actualizada en base de datos y notificada.",
    );
  };
  const upload = async (f) => {
    if (!f) return;
    try {
      const d = new FormData();
      d.append("file", f);
      const r = await api.post("/api/director/marketplace/media", d, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setModal((m) => ({
        ...m,
        product: {
          ...m.product,
          images: [...(m.product.images || []), r.data.url].slice(0, 6),
        },
      }));
    } catch (e) {
      setError(formatApiError(e));
    }
  };
  const visibility = (p, k, v) =>
    run(
      () =>
        api.patch(`/api/director/marketplace/products/${p.slug}/visibility`, {
          [k]: v,
          reason: `${k}: ${v ? "visible" : "oculto"} por Dirección KD9`,
        }),
      "Visibilidad actualizada, auditada y notificada.",
    );
  const commission = (e) => {
    e.preventDefault();
    return run(
      () =>
        api.post(
          `/api/director/marketplace/products/${modal.product.slug}/commission`,
          { percent: Number(modal.text) },
        ),
      "Comisión actualizada y notificada.",
    );
  };
  const btn = "kd9-btn-outline px-3 py-2 rounded text-[10px]";
  return (
    <PageShell
      kicker={
        director
          ? "Director KD9 · Gobierno Marketplace"
          : "Administrador · Marketplace"
      }
      title={
        director
          ? "CENTRO DE GOBIERNO MARKETPLACE KD9"
          : "PRODUCTOS EN REVISIÓN"
      }
      desc="El Director gobierna el Marketplace; cada producto que entra, cambia, se muestra, se vende o se oculta, pasa por una acción clara, registrada y notificada."
      testId="marketplace-governance-page"
    >
      {error && <div className="kd9-card p-4 mb-4 text-red-300">{error}</div>}
      {notice && (
        <div className="kd9-card p-4 mb-4 text-green-300">{notice}</div>
      )}
      {director && (
        <div className="kd9-governance-metrics grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {[
            ["Por revisar", data.summary.pending_review],
            ["Catálogo", data.summary.catalog],
            ["En tienda", data.summary.store_visible],
            ["En landing", data.summary.landing_visible],
          ].map((x) => (
            <div className="kd9-card p-4" key={x[0]}>
              <small>{x[0]}</small>
              <div className="font-cinzel text-2xl kd9-gold-text">
                {x[1] || 0}
              </div>
            </div>
          ))}
        </div>
      )}
      {director && <nav className="kd9-card mb-4 grid grid-cols-3 gap-2 p-2 sm:flex sm:overflow-x-auto" aria-label="Consola Marketplace Director">{[
        ["Inicio", "review"], ["Revisión", "review"], ["Catálogo", "catalog"], ["Ventas", "sales"], ["Auditoría", "integrity"], ["Control", "store"], ["Más", "history"],
      ].map(([label, target]) => <button key={label} onClick={() => setTab(target)} className={`${tab === target ? "kd9-btn-primary" : "kd9-btn-outline"} min-h-11 rounded-lg px-4 font-mont text-[10px] uppercase tracking-wide whitespace-nowrap`}>{label}</button>)}</nav>}
      <div className="kd9-governance-tabs kd9-card mb-6 grid grid-cols-2 gap-2 p-2 sm:flex sm:overflow-x-auto">
        {(director ? TABS : TABS.slice(0, 1)).map(([id, l, I]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`${tab === id ? "kd9-btn-primary" : "kd9-btn-outline"} px-4 py-3 rounded text-[10px] whitespace-nowrap flex gap-2`}
          >
            <I size={14} />
            {l}
          </button>
        ))}
      </div>
      {["review", "catalog", "edit", "store", "landing", "commissions"].includes(tab) && <label className="kd9-card mb-4 flex items-center gap-3 p-3"><Search size={16} className="text-cyan-400" /><input className="min-w-0 flex-1 bg-transparent font-mont text-sm outline-none" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar producto, creador, categoría o ID" aria-label="Buscar catálogo maestro" /></label>}
      {[
        "review",
        "catalog",
        "edit",
        "store",
        "landing",
        "commissions",
      ].includes(tab) && (
        <div className="space-y-3">
          {!rows.length && (
            <div className="kd9-card p-10 text-center">No hay registros.</div>
          )}
          {rows.map((p) => (
            <Product key={p.slug} p={p}>
              {tab === "review" && (
                <>
                  <button className={btn} onClick={() => openProduct(p, "detail")}>
                    Revisar
                  </button>
                  <button className={btn} onClick={() => openProduct(p, "preview")}>
                    Vista comprador
                  </button>
                  {canDecide && (
                    <>
                      <button
                        className={btn}
                        onClick={() =>
                          setModal({
                            type: "edit",
                            product: { ...p },
                            text: "",
                          })
                        }
                      >
                        Intervenir
                      </button>
                      <button
                        className={btn}
                        onClick={() =>
                          setModal({ type: "approve", product: p, text: "" })
                        }
                      >
                        Aprobar
                      </button>
                      <button
                        className={btn}
                        onClick={() =>
                          setModal({ type: "return", product: p, text: "" })
                        }
                      >
                        Devolver
                      </button>
                      <button
                        className={btn}
                        onClick={() =>
                          setModal({ type: "reject", product: p, text: "" })
                        }
                      >
                        Rechazar
                      </button>
                    </>
                  )}
                </>
              )}
              {tab === "catalog" &&
                (!p.archived_at ? (
                  <button
                    className={btn}
                    onClick={() =>
                      setModal({ type: "archive", product: p, text: "" })
                    }
                  >
                    <Archive size={12} className="inline" /> Archivar
                  </button>
                ) : (
                  <button
                    className={btn}
                    onClick={() =>
                      setModal({ type: "restore", product: p, text: "" })
                    }
                  >
                    <RotateCcw size={12} className="inline" /> Restaurar
                  </button>
                ))}
              {tab === "edit" && !p.archived_at && (
                <button
                  className={btn}
                  onClick={() =>
                    setModal({ type: "edit", product: { ...p }, text: "" })
                  }
                >
                  Editar ficha
                </button>
              )}
              {tab === "store" && (
                <button
                  disabled={p.status !== "approved" || p.archived_at}
                  className={btn}
                  onClick={() =>
                    visibility(p, "store_visible", !p.store_visible)
                  }
                >
                  {p.store_visible ? "Visible en tienda" : "Oculto en tienda"}
                </button>
              )}
              {tab === "landing" && (
                <>
                  <button
                    disabled={p.status !== "approved" || p.archived_at}
                    className={btn}
                    onClick={() =>
                      visibility(p, "landing_visible", !p.landing_visible)
                    }
                  >
                    {p.landing_visible
                      ? "Visible en landing"
                      : "Oculto en landing"}
                  </button>
                  <button
                    disabled={!p.landing_visible}
                    className={btn}
                    onClick={() => visibility(p, "featured", !p.featured)}
                  >
                    {p.featured ? "Destacado" : "No destacado"}
                  </button>
                </>
              )}
              {tab === "commissions" && !p.archived_at && (
                <button
                  className={btn}
                  onClick={() =>
                    setModal({
                      type: "commission",
                      product: p,
                      text: p.commission_percent,
                    })
                  }
                >
                  Cambiar comisión
                </button>
              )}
            </Product>
          ))}
        </div>
      )}
      {tab === "sales" && <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          {[
            ["Pedidos", data.summary.orders_total || 0],
            ["Por preparar", data.summary.orders_paid || 0],
            ["Preparando", data.summary.orders_preparing || 0],
            ["Enviados", data.summary.orders_shipped || 0],
            ["Entregados", data.summary.orders_delivered || 0],
          ].map(([label,value])=><div key={label} className="kd9-card p-4"><small>{label}</small><div className="font-cinzel text-2xl kd9-gold-text">{value}</div></div>)}
        </div>
        {(data.orders || []).length ? data.orders.map((order) => <article key={order.id} className="kd9-card p-4">
          <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
            <div>
              <strong className="font-cinzel">{order.product_name}</strong>
              <p className="font-mont text-[10px]" style={{ color: "var(--kd9-text-soft)" }}>Pedido {order.id} · {labels[order.status] || order.status} · {order.quantity} unidad(es) · v{order.status_version || 0}</p>
              <p className="mt-1 font-mont text-[9px]">Creador {order.creator_id} · Usuario {order.user_id}</p>
              {order.tracking_code && <p className="mt-1 font-mont text-[9px] text-cyan-300">{order.carrier || "Transportadora"} · {order.tracking_code}</p>}
            </div>
            <strong className="font-mont text-cyan-400">{energy(order.total_energy)}</strong>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 border-t pt-3" style={{ borderColor: "var(--kd9-border)" }}>
            {(order.status_history || []).map((event,index)=><span key={`${event.status}-${index}`} className="rounded-full border border-white/10 px-3 py-1 font-mont text-[8px] uppercase">{labels[event.status] || event.status} · {event.at ? date(event.at) : "—"}</span>)}
          </div>
        </article>) : <div className="kd9-card p-10 text-center">No hay ventas registradas.</div>}
      </div>}
      {tab === "integrity" && (
        <div className="space-y-4" data-testid="marketplace-integrity-panel">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            {[
              ["Pedidos revisados", integrity.summary.orders || 0],
              ["Íntegros", integrity.summary.ok || 0],
              ["Atención", integrity.summary.attention || 0],
              ["Legacy", integrity.summary.legacy || 0],
              ["Anomalías", integrity.summary.anomalies || 0],
            ].map(([label, value]) => (
              <div key={label} className="kd9-card p-4">
                <small>{label}</small>
                <div className="font-cinzel text-2xl kd9-gold-text">{value}</div>
              </div>
            ))}
          </div>
          <div className="kd9-card p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-cinzel">Conciliación cruzada Marketplace</div>
              <p className="mt-1 font-mont text-[10px] opacity-60">Pedido ↔ Wallet Ledger ↔ Inventario ↔ Eventos ↔ Ganancia Creador ↔ Auditoría.</p>
            </div>
            <button onClick={loadIntegrity} className="kd9-btn-outline rounded-lg px-4 py-2 font-mont text-[9px]">Reconciliar ahora</button>
          </div>
          {(integrity.items || []).map((row) => (
            <article key={row.order.id} className="kd9-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-cinzel">{row.order.product_name}</div>
                  <p className="font-mont text-[9px] opacity-60">Pedido {row.order.id} · Trace {row.trace.trace_id || "legacy"} · {labels[row.order.status] || row.order.status}</p>
                </div>
                <span className={`rounded-full border px-3 py-1 font-mont text-[8px] uppercase ${row.integrity_status === "ok" ? "border-emerald-400/30 text-emerald-300" : row.integrity_status === "legacy" ? "border-amber-400/30 text-amber-300" : "border-red-400/30 text-red-300"}`}>
                  {row.integrity_status === "ok" ? <ShieldCheck size={12} className="mr-1 inline" /> : <AlertTriangle size={12} className="mr-1 inline" />}
                  {row.integrity_status}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
                {[
                  ["Wallet", row.counts.energy_ledger],
                  ["Inventario", row.counts.inventory_movements],
                  ["Estados", row.counts.order_events],
                  ["Ganancia", row.counts.creator_earnings],
                  ["Auditoría", row.counts.audit_events],
                ].map(([label, value]) => <div key={label} className="rounded-lg border border-white/10 p-2 font-mont text-[9px]">{label}: <strong>{value}</strong></div>)}
              </div>
              {row.anomalies?.length > 0 && <div className="mt-3 rounded-lg border border-red-400/20 bg-red-500/5 p-3 font-mont text-[9px] text-red-200">{row.anomalies.join(" · ")}</div>}
            </article>
          ))}
          {!integrity.items?.length && <div className="kd9-card p-10 text-center">No hay pedidos para conciliar.</div>}
        </div>
      )}
      {tab === "history" && (
        <div className="space-y-2">
          {data.history.map((h) => (
            <div className="kd9-card p-4 flex justify-between" key={h.id}>
              <div>
                <b>{h.action}</b>
                <small className="block">
                  {h.target} · Actor {h.actor_id}
                </small>
                <p>{h.payload?.reason || h.payload?.notes}</p>
              </div>
              <small>
                {date(h.at)}
                <br />
                Notificación: {h.notification_status}
              </small>
            </div>
          ))}
        </div>
      )}
      {modal?.type === "preview" && (
        <Modal title="Vista previa como comprador" wide close={() => setModal(null)}>
          <article className="mx-auto max-w-sm overflow-hidden rounded-2xl border" style={{ borderColor: "var(--kd9-border)", background: "var(--kd9-card)" }}>
            <div className="aspect-square overflow-hidden">{modal.product.images?.[0] ? <img src={url(modal.product.images[0])} alt={modal.product.name} className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center"><ImageOff /></div>}</div>
            <div className="p-4"><div className="font-mont text-[10px] uppercase text-cyan-400">{modal.product.category}</div><h3 className="mt-1 font-cinzel text-xl">{modal.product.name}</h3><p className="mt-2 font-mont text-xs" style={{ color: "var(--kd9-text-soft)" }}>Vendido por {modal.product.creator_name}</p><strong className="mt-4 block font-mont text-xl text-cyan-400">{energy(modal.product.energy_cost)}</strong></div>
          </article>
        </Modal>
      )}
      {modal?.type === "detail" && (
        <Modal
          title="Vista como comprador · ficha e historial"
          wide
          close={() => setModal(null)}
        >
          <div className="grid md:grid-cols-2 gap-5">
            {modal.product.images?.[0] && (
              <img
                src={url(modal.product.images[0])}
                alt=""
                className="w-full aspect-square max-h-80 object-cover rounded-2xl"
              />
            )}
            <div>
              <h3 className="font-cinzel text-2xl">{modal.product.name}</h3>
              <p className="font-cormorant text-lg my-3">
                {modal.product.description}
              </p>
              <p>
                {energy(modal.product.energy_cost)} · {modal.product.category} ·{" "}
                {modal.product.creator_name}
              </p>
            </div>
          </div>
          <h3 className="font-cinzel mt-6">Historial</h3>
          {modal.history.map((h) => (
            <div className="border-t py-2" key={h.id}>
              {h.action} · {date(h.at)}
            </div>
          ))}
        </Modal>
      )}
      {modal?.type === "edit" && (
        <Modal
          title="Editar producto como Director"
          wide
          close={() => setModal(null)}
        >
          <form onSubmit={edit} className="grid md:grid-cols-2 gap-4">
            {["name", "category", "energy_cost", "stock"].map((k) => (
              <input
                key={k}
                className="kd9-input"
                type={["price", "stock"].includes(k) ? "number" : "text"}
                required
                value={modal.product[k]}
                onChange={(e) =>
                  setModal({
                    ...modal,
                    product: { ...modal.product, [k]: e.target.value },
                  })
                }
              />
            ))}
            <textarea
              className="kd9-input md:col-span-2 min-h-28"
              required
              value={modal.product.description}
              onChange={(e) =>
                setModal({
                  ...modal,
                  product: { ...modal.product, description: e.target.value },
                })
              }
            />
            <div className="md:col-span-2 flex gap-2 flex-wrap">
              {(modal.product.images || []).map((s, i) => (
                <button
                  type="button"
                  key={s + i}
                  onClick={() =>
                    setModal({
                      ...modal,
                      product: {
                        ...modal.product,
                        images: modal.product.images.filter((_, x) => x !== i),
                      },
                    })
                  }
                >
                  <img src={url(s)} alt="" className="w-20 h-20 object-cover" />
                </button>
              ))}
              <label className="w-20 h-20 border flex items-center justify-center">
                + imagen
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(e) => upload(e.target.files?.[0])}
                />
              </label>
            </div>
            <textarea
              className="kd9-input md:col-span-2"
              required
              minLength={3}
              placeholder="Motivo obligatorio"
              value={modal.text}
              onChange={(e) => setModal({ ...modal, text: e.target.value })}
            />
            <button
              className="kd9-btn-primary py-3 md:col-span-2"
              disabled={busy}
            >
              Guardar y notificar
            </button>
          </form>
        </Modal>
      )}
      {["approve", "return", "reject", "archive", "restore"].includes(modal?.type) && (
        <Modal title={modal.type} close={() => setModal(null)}>
          <form onSubmit={decide}>
            <p>{modal.product.name}</p>
            <textarea
              className="kd9-input my-4"
              required={modal.type !== "approve"}
              minLength={modal.type === "approve" ? 0 : 3}
              placeholder="Motivo o nota"
              value={modal.text}
              onChange={(e) => setModal({ ...modal, text: e.target.value })}
            />
            <button className="kd9-btn-primary w-full py-3">
              <Check className="inline" /> Confirmar y notificar
            </button>
          </form>
        </Modal>
      )}
      {modal?.type === "commission" && (
        <Modal title="Comisión Marketplace" close={() => setModal(null)}>
          <form onSubmit={commission}>
            <input
              className="kd9-input"
              type="number"
              min="0"
              max="50"
              step=".1"
              required
              value={modal.text}
              onChange={(e) => setModal({ ...modal, text: e.target.value })}
            />
            <button className="kd9-btn-primary w-full py-3 mt-4">
              Guardar y notificar
            </button>
          </form>
        </Modal>
      )}
    </PageShell>
  );
}
